var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import './vitesse-icon';
import './vitesse-provider-logo';
import { providerName, providerOf } from './vitesse-provider-logo';
import { statusBadge, statusBadgeStyles } from '../components/templates';
import { findModel } from '../ai/model-catalog';
/** Compact $/Mtok — "$2.5", "$0.15", "$0" — for the in/out price tag. */
export function fmtPrice(v) {
    if (v === 0)
        return '$0';
    if (v < 0.01)
        return `$${v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')}`;
    if (v < 1)
        return `$${v.toFixed(2).replace(/0$/, '')}`;
    return `$${Number(v.toFixed(2))}`;
}
/**
 * The right-aligned price tag for a model row: "Free" | "$2.5 / $10" (in / out per Mtok) | "$0.03"
 * when only one side is known | '' when neither is.
 *
 * ONE SIDE IS NOT ZERO ON THE OTHER. This defaulted each missing figure to 0, so an IMAGE model —
 * which OpenRouter prices on completion alone — rendered "$0 / $0.03": a free input price, stated
 * as a measurement, next to a real one. That is the same absent-is-not-zero rule the usage figures
 * follow, arriving in the picker instead of the ledger, and on the image rail it was every row.
 *
 * A single figure is also the honest reading there: what a picture costs is the question somebody
 * choosing an image model is asking, and a pair implies a per-token input price that does not
 * exist for these.
 */
export function priceTag(m) {
    if (m.free)
        return 'Free';
    const { inputPer1M: i, outputPer1M: o } = m;
    if (i === undefined && o === undefined)
        return '';
    if (i === undefined)
        return fmtPrice(o);
    if (o === undefined)
        return fmtPrice(i);
    return `${fmtPrice(i)} / ${fmtPrice(o)}`;
}
/** Human context-window size: 1_000_000 → "1M", 200_000 → "200K". */
export function fmtCtx(n) {
    if (n >= 1_000_000)
        return `${Number((n / 1_000_000).toFixed(1).replace(/\.0$/, ''))}M`;
    if (n >= 1000)
        return `${Math.round(n / 1000)}K`;
    return String(n);
}
/** Capability chips for a model, in display order (only present ones). */
export function modelCaps(m) {
    if (!m)
        return [];
    const out = [];
    if (m.ctx)
        out.push(`${fmtCtx(m.ctx)} context`);
    if (m.files)
        out.push('files');
    if (m.reasoning)
        out.push('reasoning');
    return out;
}
/** The shared two-pane picker styles (trigger + rail|rows popover) — used by
 *  <vitesse-model-select> AND <vitesse-tool-select> so the two read identically. */
export const twoPaneSelectStyles = css `
    :host {
      position: relative;
      display: block;
    }
    .trigger {
      display: flex;
      align-items: center;
      gap: 9px;
      width: 100%;
      box-sizing: border-box;
      padding: 10px 12px;
      border: 1px solid var(--border-color-light);
      border-radius: var(--radius, 6px);
      background: var(--bg-primary);
      color: var(--text-primary);
      cursor: pointer;
      font: inherit;
      font-size: 14px;
      transition: border-color 0.18s ease;
    }
    .trigger:hover,
    .trigger.open {
      border-color: var(--blue);
    }
    .val {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: left;
    }
    .val.ph {
      color: var(--text-secondary);
    }
    /* An inherited model's logo is dimmed with its label — same information, visibly not a choice. */
    .logo-ph {
      opacity: 0.7;
    }
    .x {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      padding: 0;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: var(--text-tertiary);
      cursor: pointer;
    }
    .x:hover {
      color: var(--red);
      background: color-mix(in srgb, var(--red) 12%, transparent);
    }
    .chev {
      color: var(--text-secondary);
      flex-shrink: 0;
      transition: transform 0.18s ease;
    }
    .chev.open {
      transform: rotate(180deg);
    }
    :host([disabled]) .trigger {
      opacity: 0.55;
      cursor: not-allowed;
      pointer-events: none;
    }
    /* COMPACT — the same picker sitting in a composer toolbar rather than a settings form. Only the
       TRIGGER shrinks: the popover is identical, so the thing a user learns in the AI dashboard is
       the thing they get in the rail. A separate small picker would be a second component to keep
       in sync, which is how two pickers drift apart. */
    :host([compact]) {
      display: inline-block;
      min-width: 0;
    }
    :host([compact]) .trigger {
      padding: 4px 7px;
      gap: 5px;
      font-size: 12px;
      border-radius: 8px;
      background: transparent;
      border-color: transparent;
      color: var(--text-secondary);
    }
    :host([compact]) .trigger:hover,
    :host([compact]) .trigger.open {
      background: var(--hover-bg, rgba(255, 255, 255, 0.06));
      border-color: var(--border-color-light);
      color: var(--text-primary);
    }
    /* MENU ROW — reads as a line in a menu, not a form field. No border, full width, and the
       chevron points where the panel actually opens. */
    :host([row]) {
      display: block;
    }
    :host([row]) .trigger {
      padding: 8px 9px;
      gap: 9px;
      border-color: transparent;
      border-radius: 9px;
      background: transparent;
      font-size: 13.5px;
    }
    :host([row]) .trigger:hover,
    :host([row]) .trigger.open {
      background: var(--hover-bg, rgba(255, 255, 255, 0.06));
      border-color: transparent;
    }
    :host([row]) .val {
      flex: 0 1 auto;
    }
    /* Exposed as a part so a MENU can align several rows' badges to one width without reaching into
       this shadow root — the width is the menu's concern, the colour and content are this one's. */
    .badgewrap {
      flex-shrink: 0;
      margin-left: auto;
      display: inline-flex;
      justify-content: center;
    }
    .badgewrap .base-status-badge {
      width: 100%;
      justify-content: center;
    }
    .chev.right {
      transform: none;
      color: var(--text-tertiary);
    }
    /* Two-pane popover: content-fit provider rail | stable-width models pane. Height is set
       inline by position() — provider-count-driven (min 4 / max 8 rows), the ONE authority. */
    .panel {
      display: flex;
      max-width: min(560px, 94vw);
      background: var(--card-bg);
      border: 1px solid var(--border-color);
      border-radius: 10px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      padding: 0;
      margin: 0;
      overflow: hidden;
      animation: in 0.15s ease-out;
    }
    .panel:not(:popover-open) {
      display: none;
    }
    @keyframes in {
      from {
        opacity: 0;
        transform: translateY(-4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .rail {
      flex-shrink: 0;
      width: max-content;
      overflow-y: auto;
      padding: 5px;
      border-right: 1px solid var(--border-color);
      background: var(--bg-secondary);
    }
    .models {
      /* Stable width so switching provider never reflows the panel (kills the jitter). */
      width: 264px;
      overflow-y: auto;
      padding: 5px;
    }
    /* The base scrollbar (styles/_reset.scss), replicated for the shadow-DOM panes:
       thin + invisible until the pane is hovered. */
    .rail,
    .models {
      scrollbar-width: thin;
      scrollbar-color: transparent transparent;
    }
    .rail::-webkit-scrollbar,
    .models::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    .rail::-webkit-scrollbar-track,
    .models::-webkit-scrollbar-track {
      background: transparent;
    }
    .rail::-webkit-scrollbar-thumb,
    .models::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 4px;
    }
    .rail:hover,
    .models:hover {
      scrollbar-color: var(--scrollbar-thumb) transparent;
    }
    .rail:hover::-webkit-scrollbar-thumb,
    .models:hover::-webkit-scrollbar-thumb {
      background: var(--scrollbar-thumb);
    }
    .rail:hover::-webkit-scrollbar-thumb:hover,
    .models:hover::-webkit-scrollbar-thumb:hover {
      background: var(--scrollbar-thumb-hover);
    }
    /* Rows copy the Tree exactly: 6/8 padding, radius 6, 14px, hover = --hover-bg +
       text-primary. Selected model = blue, no check. */
    .prov,
    .mrow {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      box-sizing: border-box;
      padding: 6px 8px;
      min-height: 32px;
      border: none;
      border-radius: 6px;
      background: transparent;
      cursor: pointer;
      font: inherit;
      font-size: 14px;
      color: var(--text-secondary);
      text-align: left;
      white-space: nowrap;
      transition: background-color 0.15s ease;
    }
    .prov:hover,
    .prov.active,
    .mrow:hover {
      background: var(--hover-bg, rgba(255, 255, 255, 0.06));
      color: var(--text-primary);
    }
    .mrow.sel {
      color: var(--blue);
    }
    /* Banned (governance): greyed, unclickable-looking, hover title explains. */
    .prov.banned,
    .mrow.banned {
      opacity: 0.42;
      cursor: not-allowed;
    }
    .mrow.banned:hover {
      background: transparent;
      color: var(--text-secondary);
    }
    .mlbl {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding-right: 6px;
      overflow: hidden;
    }
    .mlbl .name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .caps {
      font-size: 11px;
      color: var(--text-tertiary);
      font-weight: 400;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    /* Right-aligned live price tag (input / output per 1M tokens) — the shared grey
       BaseStatusBadge, tabular figures so prices align down the column. */
    .pricewrap {
      flex-shrink: 0;
      align-self: center;
    }
    .pricewrap .base-status-badge {
      font-variant-numeric: tabular-nums;
    }
    .empty {
      padding: 14px 16px;
      font-size: 13px;
      color: var(--text-secondary);
      white-space: nowrap;
    }
  `;
let VitesseModelSelect = class VitesseModelSelect extends LitElement {
    constructor() {
        super(...arguments);
        this.models = [];
        this.value = '';
        this.placeholder = 'Select a model';
        this.disabled = false;
        this.clearable = false;
        /** Rail rows become pickable: clicking a provider emits `pick-provider` (the ban-a-provider UX)
         *  instead of only switching the models pane. Hover still switches panes. */
        this.providerPick = false;
        /** Command-palette mode (the ban picker): picking emits `change` but the trigger never holds the
         *  value — it stays on the placeholder. */
        this.stateless = false;
        /** Toolbar sizing: a smaller trigger for sitting inline next to other controls. The popover is
         *  unchanged — this is a size, not a different picker. */
        this.compact = false;
        /** Menu-row presentation: a full-width row inside a menu, pointing RIGHT because that is where
         *  its panel opens. Still the same picker — a second one built for menus would be a second thing
         *  to keep in step with this. */
        this.row = false;
        /**
         * The model this picker INHERITS when nothing is chosen.
         *
         * A value, not a label — so the trigger can resolve its provider and show the same logo a chosen
         * model gets. An inherited default with no logo beside it reads as "nothing is set", when in fact
         * something very specific is: an empty picker and an inheriting one look identical otherwise, and
         * they mean completely different things.
         *
         * Rendered in the placeholder's muted colour, which is what distinguishes inherited from chosen.
         */
        this.placeholderModel = '';
        /** A status badge in the trigger naming what this row selects ("Text Mode"). */
        this.badge = '';
        /** Which badge tone — the shared BaseStatusBadge palette, so this cannot invent a colour. */
        this.badgeTone = 'blue';
        /** Optional icon inside the badge. */
        this.badgeIcon = '';
        /** Hover text shown on a banned (greyed) model row. */
        this.bannedTitle = 'Banned';
        this.open = false;
        this.active = null;
        this.positioned = false; // popover positioned for the current open — reposition ONLY on scroll/resize, never on active-switch (that reflow was the jitter)
        this.onDocClick = (e) => {
            if (this.open && !e.composedPath().includes(this))
                this.open = false;
        };
        this.onKey = (e) => {
            if (e.key === 'Escape')
                this.open = false;
        };
        this.reposition = () => {
            if (this.open)
                this.position();
        };
    }
    /** Whether the two-pane panel is showing. Read by a surrounding menu so it can dismiss ONE layer
     *  at a time rather than collapsing the whole stack on a single click. */
    get isOpen() {
        return this.open;
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKey);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
        this.removeReposition();
    }
    /** The provider rail, ALPHABETICAL by display name; 'custom' (BYO) always trails. */
    providers() {
        const present = [...new Set(this.models.map((m) => m.provider))];
        const named = present.filter((p) => p !== 'custom').sort((a, b) => providerName(a).localeCompare(providerName(b)));
        return present.includes('custom') ? [...named, 'custom'] : named;
    }
    modelsFor(p) {
        return p ? this.models.filter((m) => m.provider === p) : [];
    }
    selected() {
        return findModel(this.models, this.value);
    }
    toggleOpen() {
        this.open = !this.open;
        if (this.open) {
            const sel = this.selected();
            const provs = this.providers();
            this.active = sel?.provider ?? provs[0] ?? null;
        }
    }
    emit(v) {
        if (!this.stateless)
            this.value = v;
        this.dispatchEvent(new CustomEvent('change', { detail: { value: v }, bubbles: true, composed: true }));
    }
    pick(v) {
        this.open = false;
        this.emit(v);
    }
    clear(e) {
        e.stopPropagation(); // don't toggle the popover
        this.open = false;
        this.emit('');
    }
    onProvClick(p) {
        if (this.providerPick) {
            this.open = false;
            this.dispatchEvent(new CustomEvent('pick-provider', { detail: { provider: p }, bubbles: true, composed: true }));
        }
        else {
            this.active = p;
        }
    }
    // Top-layer popover: position ONCE on open (+ on scroll/resize). Switching provider
    // re-renders the models pane but must NOT reposition — repositioning every frame while
    // the content width changed under the cursor was the "glitchy/jumpy" feedback loop.
    updated() {
        const p = this.panelEl;
        if (!p)
            return;
        if (this.open) {
            if (!this.positioned) {
                try {
                    p.showPopover();
                }
                catch {
                    /* already shown */
                }
                this.position();
                this.positioned = true;
                window.addEventListener('scroll', this.reposition, true);
                window.addEventListener('resize', this.reposition);
            }
        }
        else if (this.positioned) {
            try {
                p.hidePopover();
            }
            catch {
                /* already hidden */
            }
            this.removeReposition();
            this.positioned = false;
        }
    }
    removeReposition() {
        window.removeEventListener('scroll', this.reposition, true);
        window.removeEventListener('resize', this.reposition);
    }
    position() {
        const t = this.triggerEl;
        const p = this.panelEl;
        if (!t || !p)
            return;
        // THE layout-shift fix: the panel's HEIGHT is dictated by the PROVIDER COUNT (fixed for the
        // whole open) — flexes with the rail between MIN 4 and MAX 8 provider-rows' worth (the rail
        // scrolls past 8; under 4 the extra is fixed padding) — NEVER by whichever provider's model
        // list is showing. Both panes scroll inside that fixed height, so switching providers cannot
        // resize the panel under the cursor (which re-targeted hover → resized again → the loop).
        const ROW = 32; // .prov/.mrow min-height
        const PAD = 10; // rail padding (5 top + 5 bottom)
        const railH = this.providers().length * ROW + PAD;
        p.style.height = `${Math.max(4 * ROW + PAD, Math.min(8 * ROW + PAD, railH))}px`;
        const r = t.getBoundingClientRect();
        p.style.position = 'fixed';
        p.style.margin = '0';
        p.style.inset = 'auto';
        const w = p.offsetWidth || 340;
        const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
        p.style.left = `${left}px`;
        const below = window.innerHeight - r.bottom;
        const need = p.offsetHeight + 12;
        if (below < need && r.top > below) {
            p.style.top = 'auto';
            p.style.bottom = `${window.innerHeight - r.top + 6}px`;
        }
        else {
            p.style.bottom = 'auto';
            p.style.top = `${r.bottom + 6}px`;
        }
    }
    // The two-pane picker styles + the shared BaseStatusBadge styles (the price/Free tag on each
    // row is the same grey base status badge used across the frontends).
    static { this.styles = [twoPaneSelectStyles, statusBadgeStyles]; }
    render() {
        const sel = this.selected();
        const orphanProvider = !sel && this.value ? providerOf(this.value) : undefined;
        // What is inherited, resolved to a real catalog row so it can carry a logo and its proper label.
        const inherited = !sel && !this.value && this.placeholderModel ? findModel(this.models, this.placeholderModel) : undefined;
        const provs = this.providers();
        const activeModels = this.modelsFor(this.active);
        return html `
      <!-- div-with-button-role (not <button>): the clearable × is itself a button, and
           interactive content inside a native <button> is invalid HTML. -->
      <div
        class="trigger ${this.open ? 'open' : ''}"
        role="button"
        tabindex=${this.disabled ? -1 : 0}
        @click=${this.toggleOpen}
        @keydown=${(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.toggleOpen();
            }
        }}
      >
        ${sel || orphanProvider
            ? html `<vitesse-provider-logo .provider=${sel?.provider ?? orphanProvider} .size=${18}></vitesse-provider-logo>
              <span class="val">${sel?.label ?? this.value}</span>`
            : inherited
                ? html `<vitesse-provider-logo class="logo-ph" .provider=${inherited.provider} .size=${18}></vitesse-provider-logo>
                <span class="val ph">${inherited.label}</span>`
                : // A configured model the roster no longer offers still gets NAMED. Falling back to the
                    // generic word would hide the one fact worth knowing: something is set, and it is not
                    // available — which is a thing to go fix, not a blank to ignore.
                    html `<span class="val ph">${this.placeholderModel || this.placeholder}</span>`}
        ${this.badge
            ? html `<span class="badgewrap" part="badge">${statusBadge(this.badgeTone, this.badge, this.badgeIcon || undefined)}</span>`
            : nothing}
        ${this.clearable && this.value
            ? html `<button type="button" class="x" @click=${this.clear}><vitesse-icon name="x" size="13"></vitesse-icon></button>`
            : nothing}
        ${this.row
            ? html `<vitesse-icon class="chev right" name="chevron-right" size="16"></vitesse-icon>`
            : html `<vitesse-icon class="chev ${this.open ? 'open' : ''}" name="chevron-down" size="16"></vitesse-icon>`}
      </div>
      <div class="panel" popover="manual">
        ${provs.length === 0
            ? html `<div class="empty">${this.placeholder}</div>`
            : html `
              <div class="rail">
                ${provs.map((p) => {
                const ms = this.modelsFor(p);
                const allBanned = ms.length > 0 && ms.every((m) => m.banned);
                return html `<button
                    type="button"
                    class="prov ${p === this.active ? 'active' : ''} ${allBanned ? 'banned' : ''}"
                    title=${allBanned ? this.bannedTitle : ''}
                    @mouseenter=${() => (this.active = p)}
                    @click=${() => this.onProvClick(p)}
                  >
                    <vitesse-provider-logo .provider=${p} .size=${16}></vitesse-provider-logo>
                    <span>${providerName(p)}</span>
                  </button>`;
            })}
              </div>
              <div class="models">
                ${activeModels.length === 0
                ? html `<div class="empty">—</div>`
                : activeModels.map((m) => {
                    const caps = modelCaps(m);
                    // The "Free" state lives in the price tag now, not the caps line.
                    const subParts = [m.sub && m.sub !== 'Free' ? m.sub : '', ...caps].filter(Boolean);
                    const sub = subParts.join(' · ');
                    const price = priceTag(m);
                    return html `<button
                        type="button"
                        class="mrow ${m.value === this.value ? 'sel' : ''} ${m.banned ? 'banned' : ''}"
                        title=${m.banned ? this.bannedTitle : ''}
                        @click=${() => (m.banned ? undefined : this.pick(m.value))}
                      >
                        <span class="mlbl"><span class="name">${m.label}</span>${sub ? html `<span class="caps">${sub}</span>` : nothing}</span>
                        ${price ? html `<span class="pricewrap" title="input / output, per 1M tokens">${statusBadge('gray', price)}</span>` : nothing}
                      </button>`;
                })}
              </div>
            `}
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseModelSelect.prototype, "models", void 0);
__decorate([
    property()
], VitesseModelSelect.prototype, "value", void 0);
__decorate([
    property()
], VitesseModelSelect.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseModelSelect.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], VitesseModelSelect.prototype, "clearable", void 0);
__decorate([
    property({ type: Boolean })
], VitesseModelSelect.prototype, "providerPick", void 0);
__decorate([
    property({ type: Boolean })
], VitesseModelSelect.prototype, "stateless", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseModelSelect.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseModelSelect.prototype, "row", void 0);
__decorate([
    property()
], VitesseModelSelect.prototype, "placeholderModel", void 0);
__decorate([
    property()
], VitesseModelSelect.prototype, "badge", void 0);
__decorate([
    property()
], VitesseModelSelect.prototype, "badgeTone", void 0);
__decorate([
    property()
], VitesseModelSelect.prototype, "badgeIcon", void 0);
__decorate([
    property()
], VitesseModelSelect.prototype, "bannedTitle", void 0);
__decorate([
    state()
], VitesseModelSelect.prototype, "open", void 0);
__decorate([
    state()
], VitesseModelSelect.prototype, "active", void 0);
__decorate([
    query('.trigger')
], VitesseModelSelect.prototype, "triggerEl", void 0);
__decorate([
    query('.panel')
], VitesseModelSelect.prototype, "panelEl", void 0);
__decorate([
    query('.rail')
], VitesseModelSelect.prototype, "railEl", void 0);
VitesseModelSelect = __decorate([
    customElement('vitesse-model-select')
], VitesseModelSelect);
export { VitesseModelSelect };
//# sourceMappingURL=vitesse-model-select.js.map